import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { ApiError, errorMessage, request } from '@/shared/api/client';
import { attachmentSchema, docNodeSchema } from '@/shared/api/schemas';
import { useWorkspace } from '@/features/workspace/context';
import { toast } from '@/shared/ui/toastStore';

/**
 * «Защита данных» (backend/src/Dlp): номера карт, паспорта, СНИЛС, ИНН, счета, пароли и ключи в переписке.
 * Поле ввода знает правила своей беседы и сразу показывает плашку; сервер проверяет ещё раз при отправке.
 * Настраивают администраторы, нарушения разбирают ответственные.
 */

export const ACTIONS = ['notify', 'warn', 'mask', 'hold'] as const;
export type DlpActionName = (typeof ACTIONS)[number];
const actionSchema = z.enum(ACTIONS);

const composerRuleSchema = z.object({
  key: z.string(),
  label: z.string(),
  reason: z.string(),
  action: actionSchema,
  text: z.string(),
  custom: z.boolean(),
});
export type ComposerRule = z.infer<typeof composerRuleSchema>;
export const composerSchema = z.object({ enabled: z.boolean(), rules: z.array(composerRuleSchema), officer: z.boolean() });

export const WHERE = ['all', 'external', 'channels'] as const;
export type Where = (typeof WHERE)[number];

const ruleSchema = z.object({
  key: z.string(),
  label: z.string(),
  name: z.string(),
  custom: z.boolean(),
  on: z.boolean(),
  action: actionSchema,
  external: actionSchema.nullable(),
  where: z.enum(WHERE),
  channelIds: z.array(z.string()),
  text: z.string(),
  words: z.array(z.string()),
  mask: z.string(),
});
export type DlpRule = z.infer<typeof ruleSchema>;
export type RuleInput = Partial<Pick<DlpRule, 'on' | 'action' | 'external' | 'where' | 'channelIds' | 'text' | 'name' | 'words' | 'mask'>>;

export const settingsSchema = z.object({
  enabled: z.boolean(),
  officerIds: z.array(z.string()),
  rules: z.array(ruleSchema),
  scan: z.object({ state: z.enum(['pending', 'done']).nullable(), checked: z.number(), found: z.number() }),
});
export type DlpSettings = z.infer<typeof settingsSchema>;

export const STATUSES = ['open', 'held', 'resolved', 'false', 'released', 'deleted', 'masked', 'withdrawn'] as const;
export type IncidentStatus = (typeof STATUSES)[number];

const incidentSchema = z.object({
  id: z.string(),
  rules: z.array(z.string()),
  ruleNames: z.string(),
  action: actionSchema,
  status: z.enum(STATUSES),
  source: z.enum(['send', 'edit', 'scan']),
  external: z.boolean(),
  authorId: z.string().nullable(),
  conversationId: z.string(),
  conversationName: z.string().nullable(),
  messageId: z.string().nullable(),
  threadRootId: z.string().nullable(),
  preview: z.string(),
  createdAt: z.string(),
  decidedById: z.string().nullable(),
  decidedAt: z.string().nullable(),
});
export type Incident = z.infer<typeof incidentSchema>;
export const pageSchema = z.object({ items: z.array(incidentSchema), hasMore: z.boolean() });

export type Decision = 'release' | 'delete' | 'mask' | 'resolve' | 'dismiss';

export interface IncidentFilter {
  readonly status: 'open' | 'held' | 'done' | 'all';
  readonly rule: string;
  readonly authorId: string;
  readonly source: string;
  readonly from: string;
  readonly to: string;
}

/** Ответ сервера «автор не видел плашку» (409 `dlp_warn`): что нашли и по каким правилам. */
export interface DlpFinding {
  readonly rule: string;
  readonly label: string;
  readonly reason: string;
  readonly text: string;
  readonly found: readonly string[];
}
const findingsSchema = z.object({
  code: z.literal('dlp_warn'),
  findings: z.array(z.object({ rule: z.string(), label: z.string(), reason: z.string(), text: z.string(), found: z.array(z.string()) })),
});

export function dlpWarning(e: unknown): readonly DlpFinding[] | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const parsed = findingsSchema.safeParse(e.body);
  return parsed.success ? parsed.data.findings : null;
}

/** Ответ 202: сообщение задержано до проверки. */
export const heldSchema = z.object({ held: z.object({ id: z.string(), reason: z.string() }) });
export type Held = z.infer<typeof heldSchema>['held'];

const key = (wid: string, ...part: string[]) => ['ws', wid, 'dlp', ...part] as const;

/** Правила для поля ввода этой беседы. Пока не загрузились — плашки нет, сервер всё равно проверит. */
export function useDlpRules(conversationId: string | null) {
  const { workspace, api } = useWorkspace();
  const q = conversationId === null ? '' : `?conversationId=${conversationId}`;
  return useQuery({ queryKey: key(workspace.id, 'composer', conversationId ?? ''), queryFn: () => request('GET', `${api}/dlp${q}`, composerSchema), staleTime: 5 * 60_000 });
}

export function useDlpSettings(enabled = true) {
  const { workspace, api } = useWorkspace();
  return useQuery({ queryKey: key(workspace.id, 'settings'), queryFn: () => request('GET', `${api}/dlp/settings`, settingsSchema), enabled });
}

export function useDlpActions() {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  const saved = (data: DlpSettings) => {
    client.setQueryData(key(workspace.id, 'settings'), data);
    void client.invalidateQueries({ queryKey: key(workspace.id, 'composer') });
  };
  const onError = (e: unknown) => {
    toast(errorMessage(e));
  };
  return {
    update: useMutation({
      mutationFn: (input: { enabled?: boolean; officerIds?: readonly string[] }) => request('PATCH', `${api}/dlp/settings`, settingsSchema, { body: input }),
      onSuccess: saved,
      onError,
    }),
    saveRule: useMutation({
      mutationFn: (input: { key: string; patch: RuleInput }) => request('PUT', `${api}/dlp/rules/${input.key}`, settingsSchema, { body: input.patch }),
      onSuccess: saved,
      onError,
    }),
    createRule: useMutation({
      mutationFn: (input: RuleInput) => request('POST', `${api}/dlp/rules`, settingsSchema, { body: input }),
      onSuccess: saved,
    }),
    deleteRule: useMutation({
      mutationFn: (ruleKey: string) => request('DELETE', `${api}/dlp/rules/${ruleKey}`, settingsSchema),
      onSuccess: saved,
      onError,
    }),
    testRule: useMutation({
      mutationFn: (input: { words: readonly string[]; mask: string }) =>
        request('POST', `${api}/dlp/rules/test`, z.object({ count: z.number(), checked: z.number(), examples: z.array(z.string()) }), { body: input }),
    }),
  };
}

export function useIncidents(filter: IncidentFilter, enabled = true) {
  const { workspace, api } = useWorkspace();
  return useInfiniteQuery({
    queryKey: key(workspace.id, 'incidents', JSON.stringify(filter)),
    initialPageParam: '',
    enabled,
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({ status: filter.status });
      for (const k of ['rule', 'authorId', 'source', 'from', 'to'] as const) if (filter[k] !== '') p.set(k, filter[k]);
      if (pageParam !== '') p.set('before', pageParam);
      return request('GET', `${api}/dlp/incidents?${p.toString()}`, pageSchema);
    },
    getNextPageParam: (last) => (last.hasMore ? last.items.at(-1)?.id : undefined),
  });
}

export function useIncidentActions() {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  return {
    decide: useMutation({
      mutationFn: (input: { ids: readonly string[]; decision: Decision }) =>
        request('POST', `${api}/dlp/incidents/decide`, z.object({ done: z.array(z.string()), errors: z.array(z.object({ id: z.string(), error: z.string() })) }), { body: input }),
      onSuccess: (r) => {
        if (r.errors.length > 0) toast(r.errors[0]?.error ?? '');
        void client.invalidateQueries({ queryKey: key(workspace.id, 'incidents') });
      },
      onError: (e) => {
        toast(errorMessage(e));
      },
    }),
    reveal: useMutation({ mutationFn: (id: string) => request('POST', `${api}/dlp/incidents/${id}/reveal`, z.object({ found: z.array(z.string()) })) }),
  };
}

const withdrawnSchema = z.object({ content: docNodeSchema, attachments: z.array(attachmentSchema) });

/** Автор забирает задержанное сообщение, чтобы исправить. */
export function useWithdrawHeld() {
  const { api } = useWorkspace();
  return useMutation({ mutationFn: (id: string) => request('POST', `${api}/dlp/incidents/${id}/withdraw`, withdrawnSchema) });
}
